import { US_COUNTRY_VALUE } from "constants/addresses";
import {
  CONTACT_RELATIONSHIP,
  CONTACT_RELATIONSHIP_OTHER,
  INSURANCE_PAYMENT_SOURCE,
  MANAGED_CARE_PAYMENT_SOURCE,
  PAYMENT_SOURCE_TYPE,
  PREFERRED_LANGUAGE,
  PREFERRED_LANGUAGE_OTHER
} from "constants/patients";
import { TASK_STATUS_COMPLETED, TASK_STATUS_TODO } from "constants/staff-tasks";
import { DEFAULT_PATIENTS_LIMIT } from "services/patients";
import { type PatientNote } from "services/types/patient-notes.type";
import type {
  Patient,
  PatientFilters,
  PatientInput,
  PreferredLanguageType
} from "services/types/patients.type";
import { type StaffTask } from "services/types/staff-tasks.type";

import userPhotoFallback from "../assets/images/user.png";
import { formatClinic } from "./clinics.util";
import {
  formatStaffMemberFullName,
  formatValidQueryParamUrlCommaSeparated,
  getFullName
} from "./common";

export function formatContactRelationship(
  relationship?: string | number | null,
  relationshipOther?: string | null
) {
  if (String(relationship) === CONTACT_RELATIONSHIP_OTHER) {
    return relationshipOther || CONTACT_RELATIONSHIP.getFromCode(relationship);
  }
  return CONTACT_RELATIONSHIP.getFromCode(relationship);
}

export const formatPatient = (patient: Patient) => {
  const fullName = getFullName({
    firstName: patient.first_name,
    middleName: patient.middle_name || "",
    preferredName: patient.preferred_name || "",
    lastName: patient.last_name,
    title: patient.title,
    suffix: patient.suffix
  });

  const result = {
    ...patient,
    staff_dialog_assignee:
      patient.staff_dialog_assignee &&
      formatStaffMemberFullName(patient.staff_dialog_assignee),
    paymentSourceType: patient.payment_source_type,
    fullName,
    preferred_clinic:
      patient.preferred_clinic && formatClinic(patient.preferred_clinic),
    preferred_provider:
      patient.preferred_provider &&
      formatStaffMemberFullName(patient.preferred_provider),
    // Must be formatted alongside the primary, not just carried through: every consumer reads
    // `.fullName`, which only exists because of this call. Without it the secondary silently
    // renders blank wherever a formatted patient is used (the grid, the drawer, the header).
    secondary_preferred_provider:
      patient.secondary_preferred_provider &&
      formatStaffMemberFullName(patient.secondary_preferred_provider),
    avatar: patient.photo ?? userPhotoFallback,
    totalBalance: parseFloat(patient.balance || "0"),
    patient_staff_tasks: patient.patient_staff_tasks?.map((task) => ({
      ...formatPatientStaffTask(task),
      patient: {
        id: patient.id
      }
    })),
    patient_notes: patient.patient_notes?.map((note) => ({
      ...formatPatientNote(note),
      patient: {
        id: patient.id
      }
    }))
  };

  return result;
};

export const getFullAddress = (
  patient: Pick<
    Patient,
    | "street_address_1"
    | "street_address_2"
    | "city"
    | "state"
    | "zip_code"
    | "country"
  >,
  showStreetAddressTwo = false
) => {
  const address = [
    patient.street_address_1,
    `${
      showStreetAddressTwo && patient.street_address_2
        ? `${patient.street_address_2} `
        : ""
    }${patient.city || ""}`,
    `${patient.state || ""} ${patient.zip_code || ""}`.trim(),
    patient.country?.replace(US_COUNTRY_VALUE, "USA")
  ].filter(Boolean);

  return address.join(", ");
};

export function getPatientPaymentSourceDisplay(patient?: Patient | null) {
  if (patient === null || patient === undefined) {
    return "";
  }
  if (
    patient.payment_source_type === INSURANCE_PAYMENT_SOURCE &&
    patient.primary_insurance_policy
  ) {
    return patient.primary_insurance_policy.insurer?.name;
  } else if (
    patient.payment_source_type === MANAGED_CARE_PAYMENT_SOURCE &&
    patient.managed_care_plan
  ) {
    return patient.managed_care_plan.name;
  } else {
    return PAYMENT_SOURCE_TYPE.getFromCode(patient?.payment_source_type);
  }
}

export const formatPatientStaffTask = (task: StaffTask) => {
  const { created_by = {}, assignee = {}, patient = {} } = task;

  const fullName = patient
    ? getFullName({
        firstName: patient.first_name,
        middleName: patient.middle_name || "",
        preferredName: patient.preferred_name || "",
        lastName: patient.last_name,
        title: patient.title,
        suffix: patient.suffix
      })
    : "";

  let status = task.status;
  //if task created earlier it has status value = undefined
  if (task.completed_at && !task.status) {
    status = TASK_STATUS_COMPLETED;
  } else if (!task.completed_at && !task.status) {
    status = TASK_STATUS_TODO;
  }
  const result: StaffTask = {
    ...task,
    status,
    created_by: {
      ...created_by,
      fullName: getFullName({
        firstName: created_by.first_name || "",
        lastName: created_by.last_name || "",
        title: created_by.title,
        suffix: created_by.suffix
      })
    },
    assignees: task.assignees?.map((assignee) => ({
      ...assignee,
      user: assignee.user
        ? {
            ...assignee.user,
            fullName: getFullName({
              firstName: assignee.user.first_name || "",
              lastName: assignee.user.last_name || "",
              title: assignee.user.title,
              suffix: assignee.user.suffix
            })
          }
        : assignee.user
    })),
    assignee: assignee
      ? {
          ...assignee,
          fullName: getFullName({
            firstName: assignee.first_name || "",
            lastName: assignee.last_name || "",
            title: assignee.title,
            suffix: assignee.suffix
          })
        }
      : assignee,
    patient: task.patient
      ? {
          ...task.patient,
          fullName
        }
      : task.patient
  };

  return result;
};

export const formatPatientNote = (note: PatientNote) => {
  const { created_by = {}, patient = {} } = note;

  const fullName = getFullName({
    firstName: patient.first_name,
    middleName: patient.middle_name || "",
    preferredName: patient.preferred_name || "",
    lastName: patient.last_name,
    title: patient.title,
    suffix: patient.suffix
  });

  const result: PatientNote = {
    ...note,
    created_by: {
      ...created_by,
      fullName: getFullName({
        firstName: created_by.first_name || "",
        lastName: created_by.last_name || "",
        title: created_by.title,
        suffix: created_by.suffix
      })
    },
    patient: {
      ...note.patient,
      fullName
    }
  };

  return result;
};

export const formatPatientFilters = (filters: PatientFilters) => {
  const { customFilters, baseFilter, ...apiFilters } = filters;

  return {
    // values coming only from the apiFilters
    ids: apiFilters?.ids,
    search: apiFilters?.search,
    patient_product_serial_number: apiFilters?.patient_product_serial_number,

    // values coming from both apiFilters, baseFilter in the order of priority
    is_active: apiFilters?.is_active,
    // values coming from baseFilter - it has priority from apiFilters
    ...baseFilter,
    // values coming from custom filters shared with baseFilter - has priority
    ...(customFilters?.tag ? { tags: customFilters?.tag } : {}),
    ...(customFilters?.assignedTag
      ? { assigned_tags: customFilters?.assignedTag }
      : {}),
    ...(customFilters?.assignedTag &&
    customFilters?.assigned_tags_match === "all"
      ? { assigned_tags_match: "all" }
      : {}),
    ...(customFilters?.exclude_tags
      ? { exclude_tags: customFilters?.exclude_tags }
      : {}),
    ...(customFilters?.preferred_clinic
      ? { preferred_clinic: customFilters?.preferred_clinic }
      : {}),

    // values coming only from custom filters
    payment_source_type: customFilters?.paymentSourceType,
    preferred_provider: customFilters?.preferredProvider,
    latest_referral_source: customFilters?.referralSource,
    latest_sub_referral_source: customFilters?.subReferralSource,
    crm_segment: customFilters?.crmSegment,
    manufacturer_warranty_is_expiring_in_less_than_days:
      customFilters?.manufacturerWarrantyIsExpiringInLessThanDays,
    last_outcome: customFilters?.last_outcome,
    preferred_communication_method:
      customFilters?.preferred_communication_method,
    sex: customFilters?.sex,
    insurer_any: customFilters?.insurer,
    managed_care_plan: customFilters?.managed_care_plan,
    primary_care_physician: customFilters?.primary_care_physician,
    referring_physician: customFilters?.referring_physician,
    region: customFilters?.region,
    manufacturer: customFilters?.manufacturer,
    has_hearing_aid: customFilters?.has_hearing_aid,
    has_completed_appointment: customFilters?.has_completed_appointment,
    has_upcoming_appointment: customFilters?.has_upcoming_appointment,
    hipaa_signed: customFilters?.hipaa_signed,
    block_automation: customFilters?.block_automation,
    block_reviews: customFilters?.block_reviews,
    email_bounced: customFilters?.email_bounced,
    latest_icd10_code: customFilters?.latest_icd10_code,
    zip_code: customFilters?.zip_code,
    hl_mode: customFilters?.hl_mode,
    hl_operator: customFilters?.hl_operator,
    hl_severity: customFilters?.hl_severity,
    hl_severity_max: customFilters?.hl_severity_max,
    hl_ears: customFilters?.hl_ears,
    hl_asymmetry: customFilters?.hl_asymmetry,
    hl_sloping: customFilters?.hl_sloping,
    hl_levels: customFilters?.hl_levels,
    last_completed_appointment_type:
      customFilters?.last_completed_appointment_type,
    had_completed_appointment_type:
      customFilters?.had_completed_appointment_type,
    age_range: customFilters?.age_range
  };
};

export const getPatientsEndpointWithQueryParameters = (
  filters: PatientFilters
) => {
  const { ordering, search, patient_product_serial_number } = filters;

  // When a search is active, omit the default alphabetical ordering so the backend's
  // name-relevance ranking takes precedence (ENG-1500) — true name matches rank above
  // patients who only match on email/phone. An explicit user-chosen ordering still wins.
  const isSearchActive = Boolean(search && search.trim());
  const shouldApplyDefaultOrdering = !isSearchActive || ordering;

  const orderingString = shouldApplyDefaultOrdering
    ? [ordering, "last_name,first_name,id"].filter(Boolean).join(",")
    : ordering || "";

  const orderingParam = orderingString ? `&ordering=${orderingString}` : "";

  return `/patients/${formatValidQueryParamUrlCommaSeparated(
    formatPatientFilters({
      ...filters,
      search: search && encodeURIComponent(search),
      patient_product_serial_number:
        patient_product_serial_number &&
        encodeURIComponent(patient_product_serial_number)
    }),
    true
  )}${orderingParam}&limit=${
    filters.limit ?? DEFAULT_PATIENTS_LIMIT
  }&offset=${filters.offset ?? 0}&query=${filters.query ?? "{*}"}`;
};

/**
 * SN-19749. The label to show for a patient's preferred language: the free text when the code is
 * `other` (falling back to "Other" if it is blank), the enum label otherwise, "" when unset.
 */
export const formatPreferredLanguage = (
  code?: string | null,
  other?: string | null
): string => {
  if (!code) return "";
  if (code === PREFERRED_LANGUAGE_OTHER) {
    return other?.trim() || PREFERRED_LANGUAGE.getFromCode(code);
  }
  return PREFERRED_LANGUAGE.getFromCode(code) || code;
};

/**
 * SN-19749. Whether the header should flag the language. English and blank are the norm, so they
 * show nothing; everything else (ASL included) is what the front desk needs to see at check-in.
 */
export const shouldFlagPreferredLanguage = (code?: string | null) =>
  Boolean(code) && code !== "en";

/**
 * SN-19749. The write half of the preferred-language flag gate, shared by the profile and the
 * new-patient wizard. Returns an EMPTY object when the feature is off (an older backend 400s
 * unknown write keys, so the keys must be absent, not null). The free text is kept only for
 * `other` and trimmed; the backend clears it for any other code anyway.
 */
export const buildPreferredLanguagePayload = (
  enabled: boolean,
  code?: string | null,
  other?: string | null
): Pick<PatientInput, "preferred_language" | "preferred_language_other"> =>
  enabled
    ? {
        // Form values come from the PREFERRED_LANGUAGE options, so the cast only narrows `string`.
        preferred_language: (code || "") as PreferredLanguageType | "",
        preferred_language_other:
          code === PREFERRED_LANGUAGE_OTHER ? other?.trim() || "" : ""
      }
    : {};

/**
 * "Jane Smith, Speech Therapy" — the one rendering of a secondary provider, shared by the
 * profile read view, the patient drawer and the patients grid so the three cannot drift.
 *
 * The role is free text and frequently blank, so it is appended only when present; an absent
 * provider returns undefined rather than a stray comma, letting each caller fall back to its
 * own empty-state placeholder (an em dash, or omitting the row entirely).
 */
export const formatSecondaryProvider = (
  fullName?: string | null,
  role?: string | null
): string | undefined => {
  if (!fullName) return undefined;
  const trimmedRole = role?.trim();
  return trimmedRole ? `${fullName}, ${trimmedRole}` : fullName;
};

/**
 * The write half of the secondary-provider flag gate, shared by both edit surfaces.
 *
 * Returns an EMPTY object when the feature is off, so the keys are absent from the request
 * body rather than present-and-null. `CustomModelSerializer` rejects unknown write keys
 * outright (`ValidateUnknownFieldsMixin`), so sending them against a backend that predates the
 * field 400s every patient save on that form — including saves that never touched these fields.
 */
export const buildSecondaryProviderPayload = (
  enabled: boolean,
  providerId?: number | null,
  role?: string | null
): {
  secondary_preferred_provider?: number | null;
  secondary_preferred_provider_role?: string;
} =>
  enabled
    ? {
        secondary_preferred_provider: providerId ?? null,
        // A role with nobody attached to it is unreachable: `formatSecondaryProvider` renders
        // nothing without a provider, so the text would persist invisibly and reappear
        // prefilled the next time someone opened the form.
        secondary_preferred_provider_role: providerId ? (role ?? "") : ""
      }
    : {};
